/**
 * The stores a board is made of, and finding the one holding a given row.
 *
 * A board is N row windows -- one per stack -- which is the case
 * `createRowWindowStore` was made a factory for. That makes one thing awkward
 * that is trivial everywhere else: the row dialog is about a single row, and
 * which window holds it depends on the row's own select value.
 *
 * The alternative was fetching the row by id and editing it through a detached
 * window. That works, and it leaves the card behind the dialog stale: the acting
 * tab is excluded from its own `rows_updated` broadcast, so nothing would ever
 * tell the stack's window what the dialog just changed. Editing a row has to go
 * through the window that is showing it.
 */
import { useEffect, useState } from 'react'

import { indexOfRow } from '../rows/rowWindow'
import type { RowWindowMember } from '../rows/rowWindowRegistry'
import type { RowWindowStore } from '../rows/rowWindowStore'

/** One stack's window, published upward so the board can address it. */
export interface StackWindow {
  key: string
  store: RowWindowStore
  member: RowWindowMember
}

/**
 * A registry the stacks put themselves into, and the board reads.
 *
 * `useState` rather than a ref, because the board re-renders when a stack
 * arrives: it cannot decide which window holds a row before knowing there is a
 * window at all.
 */
export function useStackWindows(): {
  windows: readonly StackWindow[]
  publish: (window: StackWindow) => () => void
} {
  const [windows, setWindows] = useState<readonly StackWindow[]>([])

  const publish = (window: StackWindow) => {
    setWindows((held) => [...held.filter((one) => one.key !== window.key), window])
    return () => {
      setWindows((held) => held.filter((one) => one.key !== window.key))
    }
  }

  return { windows, publish }
}

/**
 * The stack whose window is holding a row, or null.
 *
 * Subscribed to every stack rather than to one, because a row arrives in
 * whichever column its option puts it in and nothing above knows which that is
 * until the rows are there. Recomputed on any of their changes, which is also
 * what makes an edit show through the dialog: the row read out of the window is
 * the row the window currently holds.
 */
export function useHoldingStack(
  windows: readonly StackWindow[],
  rowId: number | null,
): StackWindow | null {
  const [holder, setHolder] = useState<StackWindow | null>(null)

  useEffect(() => {
    if (rowId === null) {
      setHolder(null)
      return
    }

    const look = () => {
      setHolder(
        windows.find((one) => indexOfRow(one.store.getState().window, rowId) !== -1) ??
          null,
      )
    }
    look()

    const unsubscribes = windows.map((one) => one.store.subscribe(look))
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe()
    }
  }, [windows, rowId])

  return holder
}
